import {
  lazy,
  Suspense,
  useReducer,
  useRef,
  useCallback,
  useMemo,
  useState,
  useEffect
} from "react";
import { Modal, Button, ButtonVariant, ButtonSize } from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { InputSize } from "shared/components";
import { Show } from "shared/components/helpers/Show";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { cx } from "shared/utils";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

import { useSendEmail, inboxKeys } from "../api/hooks";
import { useEnsureConversation } from "../hooks/useEnsureConversation";
import { useInboxContext } from "../context/useInboxContext";
import {
  useEmailVariables,
  useEmailTemplate
} from "../../settings-communication/api/hooks";
import type { EmailTemplate } from "../../settings-communication/api/types";
import { useContactUnitLinks, singleLeadForUnits } from "../api/crm";
import type { ContactUnitLinks } from "../api/crm";
import { EmailRoutingSelect } from "./EmailRoutingSelect";
import { LinkUnitResolver } from "./LinkUnitResolver";
import {
  collectLinkVariableKeys,
  replaceLinkTokens,
  shortUrlForUnit,
  EMAIL_LINK_KEYS
} from "./email-link-utils";
import type { LinkType } from "./link-variable-utils";
import type { MailyEditorRef } from "../../email-template-form/components/maily-editor-tab";
import { isMailyJson } from "../../email-template-form/components/maily-json-detector";
import { useInboxEmbed } from "../context/useInboxEmbed";

const MailyEditorTab = lazy(() =>
  import("../../email-template-form/components/maily-editor-tab").then((m) => ({
    default: m.MailyEditorTab
  }))
);

const HtmlEditorTab = lazy(() =>
  import("../../email-template-form/components/html-editor-tab").then((m) => ({
    default: m.HtmlEditorTab
  }))
);

const PreviewEmailTab = lazy(() =>
  import("../../email-template-form/components/preview-email-tab").then(
    (m) => ({
      default: m.PreviewEmailTab
    })
  )
);

type SendEditorTab = "markdown-editor" | "html-editor" | "preview-email";

const SEND_EDITOR_TABS: Tab<SendEditorTab>[] = [
  { value: "markdown-editor", label: "Markdown editor" },
  { value: "html-editor", label: "HTML Editor" },
  { value: "preview-email", label: "Preview email" }
];

const EDITOR_TABS_CLASS = cx(
  "gap-6 border-alpha-100",
  "[&_button]:!font-medium [&_button]:!pb-2 [&_button]:!px-0 [&_button]:!flex-none"
);

interface EditorState {
  subject: string;
  htmlBody: string;
  mailyJson: Record<string, unknown> | null;
  activeTab: SendEditorTab;
  isInitialized: boolean;
  lastEditedIn: "html-editor" | "markdown-editor" | null;
  seedMailyFromHtml: boolean;
}

type EditorAction =
  | { type: "SET_SUBJECT"; value: string }
  | { type: "SET_HTML_BODY"; value: string }
  | {
      type: "SYNC_FROM_MAILY_EDITOR";
      html: string;
      design: Record<string, unknown>;
    }
  | { type: "MARK_DIRTY" }
  | { type: "SET_TAB"; value: SendEditorTab }
  | { type: "SET_TAB_WITH_MAILY_SEED"; value: SendEditorTab }
  | { type: "INITIALIZE"; template: EmailTemplate };

const initialEditorState: EditorState = {
  subject: "",
  htmlBody: "",
  mailyJson: null,
  activeTab: "markdown-editor",
  isInitialized: false,
  lastEditedIn: null,
  seedMailyFromHtml: false
};

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case "SET_SUBJECT":
      return { ...state, subject: action.value };
    case "SET_HTML_BODY":
      return { ...state, htmlBody: action.value, lastEditedIn: "html-editor" };
    case "SYNC_FROM_MAILY_EDITOR":
      return {
        ...state,
        htmlBody: action.html,
        mailyJson: action.design,
        lastEditedIn: "markdown-editor",
        seedMailyFromHtml: false
      };
    case "MARK_DIRTY":
      if (state.lastEditedIn === "markdown-editor") return state;
      return { ...state, lastEditedIn: "markdown-editor" };
    case "SET_TAB":
      return { ...state, activeTab: action.value, seedMailyFromHtml: false };
    case "SET_TAB_WITH_MAILY_SEED":
      return { ...state, activeTab: action.value, seedMailyFromHtml: true };
    case "INITIALIZE": {
      const savedDesign = action.template.design_json ?? null;
      const hasHtml = Boolean(action.template.html_body);
      const validMailyJson = isMailyJson(savedDesign)
        ? (savedDesign as Record<string, unknown>)
        : null;
      return {
        ...state,
        subject: action.template.subject,
        htmlBody: action.template.html_body ?? "",
        mailyJson: validMailyJson,
        seedMailyFromHtml: hasHtml && validMailyJson === null,
        isInitialized: true,
        lastEditedIn: null
      };
    }
    default:
      return state;
  }
}

interface SendEmailTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  templateId: number;
  // Existing conversation to send into. Omitted in the new-conversation
  // (draft) state, where `draftContactId` is set instead and the conversation
  // is created lazily on send.
  conversationId?: string;
  draftContactId?: number;
  contactId?: string;
  // When set, the template email is threaded as a reply to this message
  // instead of starting a new email.
  replyToMessageId?: string;
  onMessageSent?: () => void;
}

export const SendEmailTemplateDialog = ({
  open,
  onClose,
  templateId,
  conversationId,
  draftContactId,
  contactId,
  replyToMessageId,
  onMessageSent
}: SendEmailTemplateDialogProps) => {
  const embed = useInboxEmbed();
  const [state, dispatch] = useReducer(editorReducer, initialEditorState);
  const mailyEditorRef = useRef<MailyEditorRef>(null);
  const sendEmailMutation = useSendEmail();
  const queryClient = useQueryClient();
  const {
    actions: { selectConversation }
  } = useInboxContext();
  const ensureConversation = useEnsureConversation(
    conversationId,
    draftContactId
  );

  const [selectedMailbox, setSelectedMailbox] = useState("");
  const [selectedToEmail, setSelectedToEmail] = useState("");
  // Unit chosen per link variable. The chosen unit's literal short URL
  // replaces the `{{link_key}}` token in the outgoing body (the BE email
  // resolver does NOT resolve link tokens for inbox sends).
  const [linkSelections, setLinkSelections] = useState<
    Record<LinkType, ContactUnitLinks | undefined>
  >({
    "lead.application_link": undefined,
    "lead.request_showing_link": undefined
  });

  // Guards re-entry across the whole async send (incl. the ensureConversation
  // POST that isPending doesn't cover); ref blocks the double-click, state
  // drives the button UI.
  const [isSending, setIsSending] = useState(false);
  const isSendingRef = useRef(false);

  const { data: template, isLoading: isLoadingTemplate } =
    useEmailTemplate(templateId);
  const { data: variables = [], isLoading: isLoadingVariables } =
    useEmailVariables("lead");
  const { data: units = [] } = useContactUnitLinks(contactId);

  // Link variables actually present in the current body (Maily JSON or the
  // raw HTML). Drives the resolver UI + send gating.
  const presentLinkKeys = useMemo(
    () =>
      collectLinkVariableKeys({
        mailyJson: state.mailyJson,
        htmlBody: state.htmlBody
      }),
    [state.mailyJson, state.htmlBody]
  );

  // Pre-select the only unit when the contact has exactly one, so the user
  // does not have to disambiguate a single option.
  useEffect(() => {
    if (units.length !== 1) return;
    const only = units[0];
    setLinkSelections((prev) => {
      let changed = false;
      const next = { ...prev };
      EMAIL_LINK_KEYS.forEach((key) => {
        if (!next[key] && shortUrlForUnit(only, key)) {
          next[key] = only;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [units]);

  const handleSelectLinkUnit = useCallback(
    (key: LinkType, unit: ContactUnitLinks) => {
      setLinkSelections((prev) => ({ ...prev, [key]: unit }));
    },
    []
  );

  const [showHtmlToMailyPrompt, setShowHtmlToMailyPrompt] = useState(false);
  const htmlToMailyAckedRef = useRef(false);

  useEffect(() => {
    if (template && !state.isInitialized)
      dispatch({ type: "INITIALIZE", template });
  }, [template, state.isInitialized]);

  // Maily renders per-block bubble menus (image resize, link edit, etc.) via
  // Tippy, which `appendTo: document.body`. Inside this dialog those menus
  // float outside the modal panel — they show up pinned to the viewport
  // bottom whenever the image is partially off-screen. The hide rule lives
  // in index.css (keyed off this body attribute) so there is no injected
  // <style> to leak; we only toggle the attribute while the dialog is open.
  // CRM-376: the rule is still global in scope (hides every Tippy root);
  // scoping it to the Maily editor host is tracked in PR review B5.
  useEffect(() => {
    if (!open) return;
    document.body.setAttribute("data-send-email-dialog-open", "true");
    return () => {
      document.body.removeAttribute("data-send-email-dialog-open");
    };
  }, [open]);

  const handleEmailSelectionChange = useCallback(
    (mailboxId: string, toEmail: string) => {
      setSelectedMailbox(mailboxId);
      setSelectedToEmail(toEmail);
    },
    []
  );

  const handleTabChange = useCallback(
    (tab: SendEditorTab) => {
      if (state.activeTab === "markdown-editor" && tab !== "markdown-editor") {
        if (mailyEditorRef.current)
          mailyEditorRef.current.exportHtml(({ design, html }) => {
            dispatch({ type: "SYNC_FROM_MAILY_EDITOR", html, design });
            dispatch({ type: "SET_TAB", value: tab });
          });
        else dispatch({ type: "SET_TAB", value: tab });
        return;
      }

      if (
        state.activeTab === "html-editor" &&
        tab === "markdown-editor" &&
        state.lastEditedIn === "html-editor"
      ) {
        if (!htmlToMailyAckedRef.current) {
          setShowHtmlToMailyPrompt(true);
          return;
        }
        dispatch({ type: "SET_TAB_WITH_MAILY_SEED", value: tab });
        return;
      }

      dispatch({ type: "SET_TAB", value: tab });
    },
    [state.activeTab, state.lastEditedIn]
  );

  const getContentAndSend = useCallback(
    async (rawHtml: string) => {
      if (isSendingRef.current) return;
      if (!selectedMailbox || !selectedToEmail) {
        toast.error("Please select a sender and recipient email");
        return;
      }

      // Replace link tokens with the chosen unit's literal short URL BEFORE
      // send. Name-variable tokens stay intact for the BE to resolve.
      const contentHtml = replaceLinkTokens(rawHtml, linkSelections);

      // Final guard: never send a body that still carries an unresolved link
      // token (e.g. a link variable inserted in the editor since the last
      // sync, before its unit was picked). Block and tell the user.
      if (collectLinkVariableKeys({ htmlBody: contentHtml }).length > 0) {
        toast.error("Choose a unit for every link before sending");
        return;
      }

      const doc = new DOMParser().parseFromString(contentHtml, "text/html");
      doc.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
      doc
        .querySelectorAll("p, li, h1, h2, h3, h4, h5, h6, tr")
        .forEach((el) => {
          el.append("\n");
        });
      const plainText = (doc.body.textContent ?? "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

      isSendingRef.current = true;
      setIsSending(true);
      try {
        // In the draft (new-conversation) state this creates the conversation
        // first; when a conversation already exists it resolves to its id.
        const resolvedConversationId = await ensureConversation();
        await sendEmailMutation.mutateAsync({
          conversation_id: resolvedConversationId,
          to_address: selectedToEmail,
          subject: state.subject || "(No subject)",
          content_plain: plainText,
          content_html: contentHtml,
          mailbox_id: selectedMailbox,
          contact_id:
            contactId && Number.isFinite(Number(contactId))
              ? Number(contactId)
              : undefined,
          // The body still carries name-variable tokens for the server to
          // resolve, so bind resolution + the activity log to the lead the
          // selected link unit(s) point at. When links resolve to more than one
          // lead (or none) it's omitted and the server matches by recipient.
          lead_id: singleLeadForUnits(Object.values(linkSelections)),
          // When replying, thread into the parent message. Subject is still
          // sent (this endpoint requires it) using the template's subject.
          ...(replyToMessageId ? { reply_to_message_id: replyToMessageId } : {})
        });

        // Draft was just materialised into a real conversation: refresh the
        // list and select it (mirrors ComposerForm's normal send path).
        if (draftContactId !== undefined && !conversationId) {
          queryClient.invalidateQueries({
            queryKey: inboxKeys.conversations()
          });
          selectConversation(resolvedConversationId);
        }

        onMessageSent?.();
        onClose();
      } catch {
        toast.error("Failed to send email");
      } finally {
        isSendingRef.current = false;
        setIsSending(false);
      }
    },
    [
      ensureConversation,
      conversationId,
      draftContactId,
      selectedMailbox,
      selectedToEmail,
      state.subject,
      contactId,
      replyToMessageId,
      linkSelections,
      sendEmailMutation.mutateAsync,
      queryClient,
      selectConversation,
      onMessageSent,
      onClose
    ]
  );

  const handleSend = useCallback(() => {
    if (sendEmailMutation.isPending || isSendingRef.current) return;

    if (state.lastEditedIn === "html-editor" || !mailyEditorRef.current) {
      getContentAndSend(state.htmlBody);
      return;
    }

    mailyEditorRef.current.exportHtml(({ html }) => {
      getContentAndSend(html);
    });
  }, [
    sendEmailMutation.isPending,
    state.lastEditedIn,
    state.htmlBody,
    getContentAndSend
  ]);

  // A present link variable is unresolved when no unit is chosen for it, or
  // the chosen unit has no link of that type. Send stays blocked until every
  // link variable resolves to a real short URL.
  const hasUnresolvedLink = presentLinkKeys.some(
    (key) => !shortUrlForUnit(linkSelections[key], key)
  );

  const isSendDisabled =
    !selectedMailbox ||
    !selectedToEmail ||
    (!state.htmlBody && !state.mailyJson) ||
    hasUnresolvedLink ||
    sendEmailMutation.isPending ||
    isSending;

  const isLoading = isLoadingTemplate || isLoadingVariables;

  return (
    <Modal
      opened={open}
      onClose={onClose}
      panelClassName="max-w-[800px] w-full max-h-[90vh] flex flex-col"
      zIndex={embed?.modalZIndex ?? 60}
    >
      <div className="flex items-center justify-between border-b border-alpha-100 px-6 py-5">
        <h2 className="text-xl font-bold text-content-1000">Send email</h2>
        <IconButton
          variant={IconButtonVariant.TEXT_BASE}
          size={IconButtonSize.SM}
          HugeIcon={Cancel01Icon}
          onClick={onClose}
        />
      </div>

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center py-20">
          <div className="animate-pulse space-y-3">
            <div className="h-4 w-48 rounded bg-alpha-100" />
            <div className="h-4 w-32 rounded bg-alpha-40" />
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto">
            <div className="border-b border-alpha-40 px-6 py-3">
              <EmailRoutingSelect
                contactId={contactId}
                onSelectionChange={handleEmailSelectionChange}
                menuPlacement="bottom"
              />
            </div>

            {/* On reply the server ignores any client subject and inherits
                "Re: <parent subject>", so hide the field to avoid a no-op
                input (mirrors ComposerForm hiding subject on reply). */}
            <Show if={!replyToMessageId}>
              <div className="border-b border-alpha-40 px-6 py-3">
                <div className="flex items-center gap-4">
                  <label
                    htmlFor="template-email-subject"
                    className="text-sm font-medium text-content-700 whitespace-nowrap"
                  >
                    Subject
                  </label>
                  <BaseInput
                    id="template-email-subject"
                    placeholder="Subject"
                    value={state.subject}
                    onChange={(v) =>
                      dispatch({ type: "SET_SUBJECT", value: v })
                    }
                    size={InputSize.BASE}
                    autoComplete="off"
                    className="flex-1"
                  />
                </div>
              </div>
            </Show>

            <Show if={presentLinkKeys.length > 0}>
              <div className="border-b border-alpha-40 px-6 py-3">
                <LinkUnitResolver
                  linkKeys={presentLinkKeys}
                  variables={variables}
                  units={units}
                  selections={linkSelections}
                  onSelect={handleSelectLinkUnit}
                />
              </div>
            </Show>

            <div className="bg-background-900 p-6">
              <div className="rounded-lg border border-alpha-100 bg-white px-8 pb-8 pt-6 shadow-xs">
                <div className="flex flex-col gap-6">
                  <Tabs
                    tabStyle="line"
                    size="md"
                    fullWidthTabs={false}
                    tabs={SEND_EDITOR_TABS}
                    active={state.activeTab}
                    onChange={handleTabChange}
                    className={EDITOR_TABS_CLASS}
                  />

                  <Suspense
                    fallback={
                      <div className="animate-pulse space-y-3 py-4">
                        <div className="h-8 w-32 rounded bg-alpha-100" />
                        <div className="h-[300px] w-full rounded bg-alpha-40" />
                      </div>
                    }
                  >
                    <Show
                      if={
                        state.activeTab === "markdown-editor" &&
                        state.isInitialized &&
                        !isLoadingVariables
                      }
                    >
                      <MailyEditorTab
                        ref={mailyEditorRef}
                        mailyJson={state.mailyJson}
                        seedHtml={
                          state.seedMailyFromHtml ? state.htmlBody : undefined
                        }
                        variables={variables}
                        onDirty={() => dispatch({ type: "MARK_DIRTY" })}
                      />
                    </Show>
                    <Show
                      if={
                        state.activeTab === "html-editor" && !isLoadingVariables
                      }
                    >
                      <HtmlEditorTab
                        value={state.htmlBody}
                        variables={variables}
                        onChange={(v: string) =>
                          dispatch({ type: "SET_HTML_BODY", value: v })
                        }
                      />
                    </Show>
                    <Show if={state.activeTab === "preview-email"}>
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        Preview shown without variable substitution. Variables
                        will be filled at send time from the lead matching the
                        selected link unit (or the recipient).
                      </div>
                      <PreviewEmailTab
                        htmlBody={state.htmlBody}
                        variables={variables}
                      />
                    </Show>
                  </Suspense>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-alpha-100 px-6 py-4">
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.BASE}
              onClick={onClose}
              disabled={sendEmailMutation.isPending || isSending}
            >
              Cancel
            </Button>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.BASE}
              onClick={handleSend}
              disabled={isSendDisabled}
              loading={sendEmailMutation.isPending || isSending}
            >
              Send
            </Button>
          </div>
        </>
      )}

      {showHtmlToMailyPrompt ? (
        <Modal
          opened={showHtmlToMailyPrompt}
          onClose={() => setShowHtmlToMailyPrompt(false)}
          panelClassName="max-w-[366px] w-full p-6"
          zIndex={(embed?.modalZIndex ?? 60) + 10}
        >
          <h1 className="text-xl font-bold text-content-1000 text-center">
            Switch to visual editor?
          </h1>
          <p className="text-sm font-medium text-content-400 mt-2 text-center">
            The visual editor will be rebuilt from your HTML. Complex styles or
            custom markup may not render exactly.
          </p>
          <div className="flex gap-2 mt-6">
            <Button
              className="w-full"
              onClick={() => setShowHtmlToMailyPrompt(false)}
            >
              Stay on HTML
            </Button>
            <Button
              className="w-full"
              variant={ButtonVariant.PRIMARY}
              onClick={() => {
                htmlToMailyAckedRef.current = true;
                setShowHtmlToMailyPrompt(false);
                dispatch({
                  type: "SET_TAB_WITH_MAILY_SEED",
                  value: "markdown-editor"
                });
              }}
            >
              Switch
            </Button>
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
};
